<template>
  <div :class="['navbar', { 'quick-start-pad': store.tutorial.quickStart }]">
    <div class="navbar__top-row">
      <report-tabs />

      <div class="navbar__actions-wrap">
        <div v-if="showDesktopNavbarTabs" class="navbar__tabs navbar__tabs--desktop">
          <navbar-events
            class="navbar__action-item"
            location="bottom start"
            :offset="[2, -20]"
            v-if="!store.user.isMember"
          >
            <template v-slot:activator="{ props, value }">
              <button
                type="button"
                v-bind="props"
                class="navbar__action-button navbar__workspaces-activator"
              >
                <ui-icon
                  class="mr-4"
                  size="13"
                  :color="isNavbarEventsRed ? $colors.rosa : $colors.white"
                  :icon="actions.events.iconAppend"
                />
                <span
                  :style="{ color: isNavbarEventsRed ? $colors.rosa : $colors.white }"
                >
                  {{ actions.events.title }}
                </span>
                <ui-chevron
                  class="ml-4 navbar__chevron"
                  :color="isNavbarEventsRed ? $colors.rosa : $colors.white"
                  :open="value"
                />
              </button>
            </template>
          </navbar-events>
          <navbar-boost-view
            v-if="store.user.featureFlags?.reportingBoostView?.enabled"
            class="navbar__action-item"
            :class="{ 'navbar__boost-view-active': store.reportBoostEnabled }"
            location="bottom start"
            :offset="[2, -20]"
          >
            <template v-slot:activator="{ props, value }">
              <button
                type="button"
                v-bind="props"
                class="navbar__action-button navbar__workspaces-activator"
              >
                <ui-icon
                  class="mr-4"
                  size="13"
                  :color="$colors.white"
                  :icon="actions.boostView.iconAppend"
                />
                <ui-chevron
                  class="ml-4 navbar__chevron"
                  :color="$colors.white"
                  :open="value"
                />
              </button>
            </template>
          </navbar-boost-view>
          <bookmark-list
            class="navbar__action-item"
            location="bottom start"
            :offset="[0, 8]"
          />

          <navbar-payment class="navbar__action-item" location="bottom start" />

          <button
            v-if="!store.liveChatOpen"
            type="button"
            class="navbar__action-button navbar__profile-tab navbar__action-item"
            @click="openLiveSupport"
          >
            <ui-icon
              class="mr-4"
              icon="fas fa-message"
              size="13"
              :color="$colors.white"
            ></ui-icon>
          </button>

          <button
            v-if="store.liveChatOpen"
            type="button"
            class="navbar__action-button navbar__profile-tab navbar__action-item"
            @click="closeLiveSupport"
          >
            <ui-icon
              class="mr-4"
              icon="fas fa-times"
              size="13"
              :color="$colors.red"
            ></ui-icon>
            {{ t('Close live chat') }}
          </button>

          <navbar-support
            class="navbar__action-item"
            :hidden-elements="['openLiveSupport']"
            :offset="[0, -2]"
            @temporary-password="temporaryPassword"
          >
            <template v-slot:activator="{ props, value }">
              <button type="button" v-bind="props" class="navbar__action-button">
                <ui-icon
                  size="16"
                  :color="$colors.white"
                  :icon="actions.support.iconAppend"
                ></ui-icon>

                <ui-chevron
                  class="ml-4 navbar__chevron"
                  :color="$colors.white"
                  :open="value"
                ></ui-chevron>
              </button>
            </template>
          </navbar-support>

          <navbar-workspaces
            class="navbar__action-item"
            location="bottom start"
            :offset="[2, -20]"
          >
            <template v-slot:activator="{ props, value }">
              <button
                type="button"
                v-bind="props"
                class="navbar__action-button navbar__workspaces-activator"
              >
                <div class="navbar__workspaces-count" v-if="showSelectedWorkspacesCount">
                  {{ store.user.selectedWorkspaces.length }}
                </div>

                <ui-icon
                  size="16"
                  :color="$colors.white"
                  :icon="actions.workspaces.iconAppend"
                ></ui-icon>

                <ui-chevron
                  class="ml-4 navbar__chevron"
                  :color="$colors.white"
                  :open="value"
                ></ui-chevron>
              </button>
            </template>
          </navbar-workspaces>

          <router-link
            :to="actions.settings.href"
            class="navbar__action-button navbar__profile-tab navbar__action-item"
            :class="{
              'v-tabs__div--active': isSettingsRoute,
            }"
          >
            <ui-icon
              size="16"
              :color="$colors.white"
              :icon="actions.settings.iconAppend"
            />
          </router-link>

          <ui-menu
            v-model="userMenu"
            class="navbar__action-item navbar__user-menu v-tabs__div-custom"
            :close-on-content-click="false"
            :items="menuList"
            :stick-to-right="true"
            location="bottom end"
            content-class="navbar__user-menu-content navbar__user-menu-content--logout"
          >
            <template v-slot:activator="{ props }">
              <button
                v-bind="props"
                type="button"
                class="navbar__action-button navbar__profile-tab"
              >
                <div class="navbar__user-menu-activator">
                  <ui-icon
                    size="16"
                    :color="$colors.white"
                    class="mr-4"
                    icon="fas fa-user"
                  />
                  <div
                    class="navbar__user-name"
                    v-html="t('Hi, <span>{0}</span>', [store.user.firstName])"
                  ></div>
                </div>
              </button>
            </template>
          </ui-menu>
        </div>

        <ui-menu
          v-if="showTabletNavbarMenu"
          :eager="true"
          v-model="tabletNavbarMenuOpen"
          class="navbar__tabs navbar__tabs--tablet"
          content-class="navbar-tablet-menu"
          location="bottom end"
          :offset="[2, 20]"
          :viewport-margin="0"
        >
          <template v-slot:activator="{ props }">
            <button
              type="button"
              v-bind="props"
              class="navbar__profile-tab navbar__profile-tab--small"
            >
              <ui-icon size="12" :color="$colors.white" icon="fas fa-user"></ui-icon>
            </button>
          </template>

          <template #menu>
            <ui-menu-content>
              <navbar-events
                v-if="!store.user.isMember"
                class="ui-menu-content-item"
                :changePlanClick="closeTabletNavbarMenu"
                location="end top"
                :offset="[0, -10]"
              >
                <template #activator="{ props: menuProps, value }">
                  <ui-menu-content-item
                    v-bind="menuProps"
                    :item="actions.events"
                    :activator-chevron="true"
                    :menu-open="value"
                  />
                </template>
              </navbar-events>

              <navbar-boost-view
                v-if="store.user.featureFlags?.reportingBoostView?.enabled"
                class="ui-menu-content-item"
                :changePlanClick="closeTabletNavbarMenu"
                location="end top"
                :offset="[0, -10]"
              >
                <template #activator="{ props: menuProps, value }">
                  <ui-menu-content-item
                    v-bind="menuProps"
                    :item="actions.boostView"
                    :activator-chevron="true"
                    :menu-open="value"
                  />
                </template>
              </navbar-boost-view>

              <ui-menu-content-item
                :item="actions.settings"
                @action="closeTabletNavbarMenu"
              ></ui-menu-content-item>

              <navbar-support
                class="ui-menu-content-item"
                :has-close-intercom="true"
                @action="closeTabletNavbarMenu"
                @temporary-password="temporaryPassword"
                location="end top"
                :offset="[0, -10]"
              >
                <template v-slot:activator="{ props, value }">
                  <ui-menu-content-item
                    v-bind="props"
                    :item="actions.support"
                    :activator-chevron="true"
                    :menu-open="value"
                  />
                </template>
              </navbar-support>

              <navbar-workspaces
                class="ui-menu-content-item"
                :closeMenu="closeTabletNavbarMenu"
                location="end top"
                :offset="[0, -10]"
              >
                <template v-slot:activator="{ props, value }">
                  <ui-menu-content-item
                    v-bind="props"
                    :item="actions.workspaces"
                    :activator-chevron="true"
                    :menu-open="value"
                  />
                </template>
              </navbar-workspaces>

              <navbar-payment
                class="ui-menu-content-item"
                @action="closeTabletNavbarMenu"
                location="end top"
                :offset="[0, -10]"
              >
                <template v-slot:activator="{ props, value }">
                  <ui-menu-content-item
                    v-bind="props"
                    :item="actions.payment"
                    :activator-chevron="true"
                    :menu-open="value"
                  />
                </template>
              </navbar-payment>

              <bookmark-list
                class="ui-menu-content-item"
                location="end top"
                :offset="[0, -10]"
              >
                <template v-slot:activator="{ props, open }">
                  <ui-menu-content-item
                    v-bind="props"
                    :item="actions.bookmarks"
                    :activator-chevron="true"
                    :menu-open="open"
                  />
                </template>
              </bookmark-list>
              <ui-menu-content-item :item="actions.logout" />
            </ui-menu-content>
          </template>
        </ui-menu>
      </div>
    </div>

    <div class="navbar__extension" v-if="!isSettingsRoute">
      <main-menu />
    </div>

    <div class="navbar__extension" v-else>
      <div class="tabs-extension main-menu settings-menu">
        <main-menu-grouping />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { useDisplay } from 'vuetify';
import { useStore } from '@/store';
import { useColors, useI18n, useLogout, useNavbarEvents, useUrl } from '@/composables';
import { useIntercom } from '@/composables/useIntercom';
import $emitter from '@/services/emitter';
import MainMenu from './main-menu.vue';
import MainMenuGrouping from '@/views/layout/navigation/grouping/main-menu-grouping.vue';
import NavbarPayment from './navigation/navbar-payment.vue';
import NavbarSupport from './navigation/navbar-support.vue';
import ReportTabs from '@/views/reports/views/report-tabs.vue';
import BookmarkList from '@/components/bookmark/bookmark-list.vue';
import NavbarWorkspaces from '@/views/layout/navigation/navbar-workspaces.vue';
import NavbarEvents from '@/views/layout/navigation/navbar-events.vue';
import NavbarBoostView from '@/views/layout/navigation/navbar-boost-view.vue';

const store = useStore();
const { lgAndUp, mdAndUp, lgAndDown } = useDisplay();
const showDesktopNavbarTabs = computed(
  () => lgAndUp.value || store.desktopViewForced === true,
);
const showTabletNavbarMenu = computed(
  () => !showDesktopNavbarTabs.value && mdAndUp.value && lgAndDown.value,
);
const { t } = useI18n();
const $colors = useColors();
const { isSettingsRoute } = useUrl();
const { openLiveSupport, closeLiveSupport } = useIntercom();
const { logout } = useLogout();

const tabletNavbarMenuOpen = ref(false);
const userMenu = ref(false);

const handleLogout = () => {
  logout({
    beforeNavigate: () => {
      tabletNavbarMenuOpen.value = false;
      userMenu.value = false;
    },
  });
};

const { isNavbarEventsRed } = useNavbarEvents();

const actions = computed(() => ({
  settings: {
    title: t('Settings'),
    iconAppend: 'fas fa-cog',
    href: '/app/profile',
    routerLink: true,
  },
  support: {
    title: t('Support'),
    iconAppend: 'fas fa-question-circle',
  },
  workspaces: {
    title: t('Workspaces'),
    iconAppend: 'fas fa-window-restore',
  },
  payment: {
    title: t('Payment'),
    iconAppend: 'fas fa-exclamation-circle',
  },
  bookmarks: {
    title: t('Bookmarks'),
    iconAppend: 'fas fa-star',
  },
  events: {
    title: t('Events'),
    iconAppend: 'fas fa-gauge',
    color: isNavbarEventsRed.value ? $colors.red : undefined,
    iconAppendColor: isNavbarEventsRed.value ? $colors.red : undefined,
  },
  boostView: {
    title: t('Boost View'),
    iconAppend: 'fas fa-rocket',
  },
  logout: {
    title: t('Logout'),
    iconAppend: 'fas fa-sign-out-alt',
    click: handleLogout,
  },
}));

const menuList = computed(() => [actions.value.logout]);

const showSelectedWorkspacesCount = computed(() => {
  const user = store.user;
  const { selectedWorkspaces, workspaces } = user;
  const selectedWorkspacesLength = selectedWorkspaces && selectedWorkspaces.length;
  return (
    selectedWorkspaces &&
    selectedWorkspaces.length > 0 &&
    workspaces &&
    workspaces.length !== selectedWorkspacesLength
  );
});

const closeTabletNavbarMenu = () => {
  tabletNavbarMenuOpen.value = false;
};

const temporaryPassword = () => {
  nextTick(() => {
    $emitter.emit('open-dialog', { name: 'TemporaryPasswordDialog' });
  });
};
</script>

<style lang="scss" scoped>
.navbar {
  position: static;
  background-color: var(--color-bluedeep);
  transition: none !important;
  box-shadow: none !important;
}

.navbar__top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 40px;
  padding: 0;
}

.navbar__actions-wrap {
  display: flex;
  flex-grow: 0;
  align-items: center;
  margin-left: auto;
}

.navbar__tabs {
  display: flex;
  align-items: center;
  line-height: normal;
  height: 40px;
}

.navbar :deep(.navbar__action-button) {
  border: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
  text-decoration: none;
  height: 40px;
  padding: 8px;
  display: flex;
  align-items: center;
  color: var(--color-white);
  text-transform: capitalize;
  font-size: 13px;
  font-weight: normal;
  white-space: nowrap;

  &:hover {
    background-color: var(--color-white20);
    color: var(--color-white);
  }

  &.v-tabs__div--active {
    background-color: var(--color-white20);
  }
}

.navbar__action-item {
  display: flex;
  align-items: stretch;
  height: 40px;
}

.navbar__extension {
  padding: 0 !important;
  background-color: var(--color-bluedeep);
}

.navbar__profile-tab--small {
  padding: 0 8px;
  height: 40px;
  display: flex;
  align-items: center;
  border: 0;
  background: transparent;
  cursor: pointer;

  &:hover {
    background-color: var(--color-white20);
  }
}

.navbar__user-menu {
  :deep(.v-menu__activator) {
    height: 100%;
  }
}

.navbar__user-menu-activator {
  display: flex;
  align-items: baseline;
  flex-wrap: nowrap;
  overflow: hidden;

  span {
    display: inline-block;
    max-width: 140px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}

.navbar__user-name {
  display: flex;
  color: var(--color-white);
}

.navbar__workspaces-activator {
  position: relative;
}

.navbar__workspaces-count {
  background: var(--color-red);
  border-radius: 100%;
  font-size: 8px;
  line-height: 12px;
  color: var(--color-white);
  min-width: 12px;
  height: 12px;
  position: absolute;
  top: 0;
  left: 16px;
  padding: 0 2px;
}

.navbar__boost-view-active {
  background: var(--color-green);
}

.navbar__chevron {
  margin-top: 3px;
}

:deep(.bookmark-list__activator-wrapper) {
  height: 40px;
}

:global(.navbar__user-menu-content) {
  border-radius: 0 !important;
  border: 0 !important;

  .v-list .v-icon {
    font-size: 11px;
    padding-right: 5px;
    color: var(--color-graphite);
  }
}

:global(.navbar__user-menu-content--logout) {
  min-width: 110px;

  .v-list-item {
    justify-content: flex-start;
  }
}

.navbar__tabs--desktop,
.navbar__tabs--tablet {
  display: flex;
  align-items: center;
}
</style>

<style lang="scss">
.settings-menu {
  width: 100%;
}

.v-overlay__content.navbar-tablet-menu {
  background: var(--color-white);
  border-radius: 8px !important;
  overflow: hidden;

  > .v-list {
    box-shadow: none !important;
    background: transparent;
  }
}
</style>
